import { useEffect, useState } from 'react';
import { getCheckoutSessionStatus, type DonationFrequency } from '@/lib/stripe-billing';

export interface DonationFrequencySource {
  id: string;
  donationFrequency: DonationFrequency;
}

interface EnrichedDonationDetails {
  donationFrequency: DonationFrequency;
  stripeSubscriptionId: string | null;
}

/**
 * Stripe is authoritative for checkout session donations when Firestore attribution is stale.
 */
export function useEnrichedDonationFrequencies(
  donations: DonationFrequencySource[]
): {
  enrichedById: Record<string, EnrichedDonationDetails>;
  enriching: boolean;
} {
  const [enrichedById, setEnrichedById] = useState<Record<string, EnrichedDonationDetails>>({});
  const [enriching, setEnriching] = useState(false);

  const donationIds = donations.map((donation) => donation.id).join('|');

  useEffect(() => {
    const checkoutSessionDonations = donations.filter((donation) => donation.id.startsWith('cs_'));
    if (checkoutSessionDonations.length === 0) {
      setEnrichedById({});
      setEnriching(false);
      return;
    }

    let cancelled = false;
    setEnriching(true);

    const enrichFromStripe = async (): Promise<void> => {
      const entries = await Promise.all(
        checkoutSessionDonations.map(async (donation) => {
          try {
            const status = await getCheckoutSessionStatus(donation.id);
            return [
              donation.id,
              {
                donationFrequency: status.donationFrequency,
                stripeSubscriptionId: status.stripeSubscriptionId,
              },
            ] as const;
          } catch (error) {
            console.warn(
              `[useEnrichedDonationFrequencies] Could not resolve Stripe checkout session "${donation.id}":`,
              error
            );
            return [
              donation.id,
              {
                donationFrequency: donation.donationFrequency,
                stripeSubscriptionId: null,
              },
            ] as const;
          }
        })
      );

      if (cancelled) return;

      setEnrichedById(Object.fromEntries(entries));
      setEnriching(false);
    };

    void enrichFromStripe();

    return () => {
      cancelled = true;
    };
    // donationIds captures list identity; donations is read from the closure when ids change.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: avoid re-fetch on array reference churn
  }, [donationIds]);

  return { enrichedById, enriching };
}

export function getEffectiveDonationFrequency(
  donation: DonationFrequencySource,
  enrichedById: Record<string, EnrichedDonationDetails>
): DonationFrequency {
  return enrichedById[donation.id]?.donationFrequency ?? donation.donationFrequency;
}

export function getEffectiveStripeSubscriptionId(
  donation: DonationFrequencySource & { stripeSubscriptionId?: string | null },
  enrichedById: Record<string, EnrichedDonationDetails>
): string | null {
  return (
    enrichedById[donation.id]?.stripeSubscriptionId ??
    donation.stripeSubscriptionId?.trim() ??
    null
  );
}
